import { useAtom, useAtomValue } from "jotai";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "chameleon/lib/components";

import {
  paymentMessagesAtom,
  paymentRequirementsAtom,
} from "checkout-experience/atoms/payment-atoms";
import { usePaymentOptionContext } from "checkout-experience/components/payment-method/payment-option-provider";
import { useChexContext } from "checkout-experience/providers/chex-context-wrapper";
import { PaymentDomain } from "checkout-query-client/api/types";
import { ChexFieldValues } from "checkout-query-client/types/chex-form-fields";
import { PaymentType } from "checkout-query-client/types/form-types";

interface PayByBankLinkBankAccountButtonProps {
  paymentDomain: PaymentDomain | undefined;
  isPayByBankLinkingInProgress: boolean;
  setIsPayByBankLinkingInProgress: (inProgress: boolean) => void;
}

const PayByBankLinkBankAccountButton = ({
  paymentDomain,
  isPayByBankLinkingInProgress,
  setIsPayByBankLinkingInProgress,
}: PayByBankLinkBankAccountButtonProps) => {
  const paymentRequirements = useAtomValue(paymentRequirementsAtom);
  const [paymentMessages, setPaymentMessages] = useAtom(paymentMessagesAtom);

  const { t } = useTranslation();
  const { watch } = useFormContext();
  const { paymentOption } = usePaymentOptionContext() ?? {};

  const { merchantSettings } = useChexContext();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(false);

  useEffect(() => {
    if (isPayByBankLinkingInProgress) {
      setIsDisabled(true);
      setIsLoading(true);
    } else {
      setIsDisabled(false);
      setIsLoading(false);
    }
  }, [isPayByBankLinkingInProgress]);

  const handlePayByBankLinkBankAccount = (): void => {
    const formFields = watch(`${paymentOption?.formId}`) as ChexFieldValues;
    setIsPayByBankLinkingInProgress(true);

    if (
      paymentRequirements.payByBankConsentUrl &&
      (paymentMessages.payByBankCompletionMessage?.type === "cancel" ||
        paymentMessages.payByBankCompletionMessage?.type === "timeout")
    ) {
      // already has consentUrl, no need to do first pay call, erase completion message to trigger modal (only iframe version has ability)
      setPaymentMessages({
        ...paymentMessages,
        payByBankCompletionMessage: undefined,
      });
    } else {
      // scenario 1 no consentUrl, do first pay call (pop up version will always do this)
      if (paymentDomain) {
        void paymentDomain({
          merchantSettings,
          paymentType: PaymentType.payByBank,
          submitType: PaymentType.payByBank,
          formFields,
        }).catch(() => {
          // error button should reset
          setIsPayByBankLinkingInProgress(false);
        });
      }
    }
  };

  return (
    <Button
      variant={"outline"}
      id={"btn-link-bank-account"}
      testId={"btn-link-bank-account"}
      position="left"
      text={t("payByBank.linkBankAccount")}
      onClick={handlePayByBankLinkBankAccount}
      isLoading={isLoading}
      isDisabled={isDisabled}
    />
  );
};
export default PayByBankLinkBankAccountButton;
